import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import '../styles/login.css';
import { getBranding } from '../config/branding';
import { Trans, useTranslation } from 'react-i18next';

const API_BASE = process.env.REACT_APP_API_URL || '';

const AcceptInvitePage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [formData, setFormData] = useState({
    password: '',
    confirm_password: '',
    first_name: '',
    last_name: '',
  });
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isValidating, setIsValidating] = useState(true);
  const [invitation, setInvitation] = useState<{ email: string; role: string } | null>(null);

  // Validate token on load
  useEffect(() => {
    if (!token) {
      setError(t('auth.errInviteLink'));
      setIsValidating(false);
      return;
    }

    const validateToken = async () => {
      try {
        const response = await fetch(`${API_BASE}/auth/validate-invite?token=${token}`);
        if (response.ok) {
          const data = await response.json();
          setInvitation(data);
        } else {
          setError(t('auth.errInviteExpired'));
        }
      } catch {
        setError(t('auth.errValidateInvite'));
      } finally {
        setIsValidating(false);
      }
    };

    validateToken();
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirm_password) {
      setError(t('auth.errPasswordsMismatch'));
      return;
    }

    if (formData.password.length < 8) {
      setError(t('auth.errPasswordShort'));
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_BASE}/auth/accept-invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          token,
          password: formData.password,
          first_name: formData.first_name,
          last_name: formData.last_name,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        localStorage.setItem('access_token', data.access_token);
        navigate('/');
      } else {
        const data = await response.json();
        setError(data.detail || t('auth.errAcceptInvite'));
      }
    } catch {
      setError(t('common.networkError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isValidating) {
    return (
      <div className="login-page">
        <div className="login-loading">
          <div className="login-spinner" />
        </div>
      </div>
    );
  }

  if (!invitation) {
    return (
      <div className="login-page">
        <div className="login-bg">
          <div className="login-bg-gradient" />
          <div className="login-bg-noise" />
          <div className="login-orb login-orb-1" />
          <div className="login-orb login-orb-2" />
          <div className="login-orb login-orb-3" />
        </div>
        <div className="login-container" style={{ maxWidth: 500 }}>
          <div className="login-form-container" style={{ flex: 'none', width: '100%' }}>
            <div className="login-form-card">
              <div className="login-error" style={{ marginBottom: 0 }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="8" x2="12" y2="12"/>
                  <line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
                <span>{error}</span>
              </div>
              <button
                className="login-submit"
                style={{ marginTop: 24 }}
                onClick={() => navigate('/login')}
              >
                {t('auth.goToLogin')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <div className="login-bg">
        <div className="login-bg-gradient" />
        <div className="login-bg-noise" />
        <div className="login-orb login-orb-1" />
        <div className="login-orb login-orb-2" />
        <div className="login-orb login-orb-3" />
      </div>

      <div className="login-container" style={{ maxWidth: 500 }}>
        <div className="login-form-container" style={{ flex: 'none', width: '100%' }}>
          <div className="login-form-card">
            <div className="login-form-header">
              <img src="/logo.png" alt={getBranding().brand_name} className="login-logo" style={{ height: 40, marginBottom: 24 }} />
              <h2>{t('auth.completeRegistration')}</h2>
              <p>
                <Trans
                  i18nKey="auth.invitedAs"
                  values={{ role: t(`auth.role_${invitation.role}`, { defaultValue: invitation.role }) }}
                  components={{ b: <strong /> }}
                />
                <br />
                <span style={{ color: 'rgba(255,255,255,0.6)' }}>{invitation.email}</span>
              </p>
            </div>

            {error && (
              <div className="login-error">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="8" x2="12" y2="12"/>
                  <line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="login-form">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="login-field">
                  <label htmlFor="first_name">{t('auth.firstName')}</label>
                  <div className="login-input-wrapper">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                      <circle cx="12" cy="7" r="4"/>
                    </svg>
                    <input
                      id="first_name"
                      type="text"
                      value={formData.first_name}
                      onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                      placeholder={t('auth.firstNamePh')}
                      required
                      autoFocus
                    />
                  </div>
                </div>

                <div className="login-field">
                  <label htmlFor="last_name">{t('auth.lastName')}</label>
                  <div className="login-input-wrapper">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                      <circle cx="12" cy="7" r="4"/>
                    </svg>
                    <input
                      id="last_name"
                      type="text"
                      value={formData.last_name}
                      onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                      placeholder={t('auth.lastNamePh')}
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="login-field">
                <label htmlFor="password">{t('login.password')}</label>
                <div className="login-input-wrapper">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                  </svg>
                  <input
                    id="password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder={t('auth.atLeast8')}
                    required
                    minLength={8}
                  />
                </div>
              </div>

              <div className="login-field">
                <label htmlFor="confirm_password">{t('auth.confirmPassword')}</label>
                <div className="login-input-wrapper">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                  </svg>
                  <input
                    id="confirm_password"
                    type="password"
                    value={formData.confirm_password}
                    onChange={(e) => setFormData({ ...formData, confirm_password: e.target.value })}
                    placeholder={t('auth.repeatPassword')}
                    required
                  />
                </div>
              </div>

              <button
                type="submit"
                className="login-submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <div className="login-submit-spinner" />
                ) : (
                  t('auth.createAccount')
                )}
              </button>
            </form>
          </div>

          <p className="login-footer-text">
            {t('auth.copyright', { year: new Date().getFullYear(), brand: getBranding().brand_name })}
          </p>
        </div>
      </div>
    </div>
  );
};

export default AcceptInvitePage;
